/**
 * =============================================================================
 * Devices Management Page
 * =============================================================================
 *
 * CRUD interface for managing RehaPiano devices.
 *
 * Features:
 * - List all devices with filters
 * - Create new device (generates API key)
 * - Edit device details with notes
 * - Regenerate API key
 *
 * Access: system_admin, institution_admin
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  devicesApi,
  institutionsApi,
  AssignableUser,
  Device,
  DeviceCreate,
  DeviceUpdate,
  DeviceCreateResponse,
} from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'

// =============================================================================
// API Key Display Component
// =============================================================================

interface ApiKeyDisplayProps {
  apiKey: string
  deviceId: string
  onClose: () => void
}

/**
 * Modal content for displaying newly created API key and device ID.
 * Shows warning that key won't be shown again.
 */
const ApiKeyDisplay: React.FC<ApiKeyDisplayProps> = ({ apiKey, deviceId, onClose }) => {
  const [copiedKey, setCopiedKey] = useState(false)
  const [copiedId, setCopiedId] = useState(false)

  const copyText = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch {
      // fall through to legacy copy
    }

    try {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      return success
    } catch {
      return false
    }
  }

  const handleCopyKey = async () => {
    const success = await copyText(apiKey)
    if (success) {
      setCopiedKey(true)
      setTimeout(() => setCopiedKey(false), 2000)
    } else {
      alert('Copy failed. Please copy the value manually.')
    }
  }

  const handleCopyId = async () => {
    const success = await copyText(deviceId)
    if (success) {
      setCopiedId(true)
      setTimeout(() => setCopiedId(false), 2000)
    } else {
      alert('Copy failed. Please copy the value manually.')
    }
  }

  return (
    <div className="space-y-4">
      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800 font-medium">
          Important: Save this API key now!
        </p>
        <p className="text-yellow-700 text-sm mt-1">
          This is the only time you'll see this key. Store it securely.
        </p>
      </div>

      {/* Device ID */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Device ID (for data purposes)
        </label>
        <div className="flex gap-2">
          <div className="flex-1 bg-gray-100 rounded-lg p-3 font-mono text-sm break-all">
            {deviceId}
          </div>
          <button onClick={handleCopyId} className="btn-secondary whitespace-nowrap">
            {copiedId ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* API Key */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          API Key
        </label>
        <div className="flex gap-2">
          <div className="flex-1 bg-gray-100 rounded-lg p-3 font-mono text-sm break-all">
            {apiKey}
          </div>
          <button onClick={handleCopyKey} className="btn-secondary whitespace-nowrap">
            {copiedKey ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-primary">
          I've Saved the Key
        </button>
      </div>
    </div>
  )
}

interface AssignUserModalProps {
  device: Device | null
  users: AssignableUser[]
  loading: boolean
  saving: boolean
  selectedUserId: string
  onUserChange: (value: string) => void
  onClose: () => void
  onAssign: () => void
}

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  device,
  users,
  loading,
  saving,
  selectedUserId,
  onUserChange,
  onClose,
  onAssign,
}) => {
  if (!device) {
    return null
  }

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
        Assign user for device <span className="font-mono">{device.serial_number}</span>.
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
        <select
          className="input"
          value={selectedUserId}
          onChange={(e) => onUserChange(e.target.value)}
          disabled={loading || saving}
        >
          <option value="">Select user</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name} ({user.auth_provider})
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onAssign}
          disabled={saving || loading || !selectedUserId}
        >
          {saving ? 'Assigning...' : 'Assign User'}
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Device Form Component
// =============================================================================

interface DeviceFormProps {
  device?: Device | null
  onSubmit: (data: DeviceCreate | DeviceUpdate) => void
  onCancel: () => void
  loading?: boolean
}

/**
 * Helper function for formatting dates.
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('sk-SK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Form for creating/editing devices.
 */
const DeviceForm: React.FC<DeviceFormProps> = ({
  device,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { hasRole } = useAuth()
  const isEdit = !!device

  // Fetch institutions for dropdown
  const { data: institutions } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => institutionsApi.list(),
    enabled: hasRole('system_admin'),
  })

  const { data: myInstitution } = useQuery({
    queryKey: ['institutions-me'],
    queryFn: () => institutionsApi.me(),
    enabled: !hasRole('system_admin'),
  })

  const [formData, setFormData] = useState({
    serial_number: device?.serial_number || '',
    device_type: device?.device_type || 'physical',
    institution_id: device?.institution_id || '',
    location: device?.location || '',
    location_type: device?.location_type || 'hospital',
    status: device?.status || 'active',
    notes: device?.notes || '',
  })

  useEffect(() => {
    if (!hasRole('system_admin') && !isEdit && !formData.institution_id && myInstitution?.data?.id) {
      setFormData((prev) => ({ ...prev, institution_id: myInstitution.data.id }))
    }
  }, [hasRole, isEdit, formData.institution_id, myInstitution])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isEdit) {
      onSubmit({
        status: formData.status,
        location: formData.location || undefined,
        location_type: formData.location_type || undefined,
        institution_id: formData.institution_id || undefined,
        notes: formData.notes || undefined,
      } as DeviceUpdate)
    } else {
      onSubmit({
        serial_number: formData.serial_number,
        device_type: formData.device_type,
        institution_id: formData.institution_id,
        location: formData.location || undefined,
        location_type: formData.location_type || undefined,
        notes: formData.notes || undefined,
      } as DeviceCreate)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Read-only device info section - only in edit mode */}
      {isEdit && device && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Device Information</h4>

          {/* Device ID - full width with copy button */}
          <div className="flex items-center gap-2 text-sm pb-2 border-b border-gray-200">
            <span className="text-gray-500">Device ID:</span>
            <span className="font-mono text-xs bg-gray-200 px-2 py-1 rounded flex-1">
              {device.id}
            </span>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(device.id)}
              className="text-primary-600 hover:text-primary-800 text-xs font-medium"
            >
              Copy
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            {/* Serial Number */}
            <div>
              <span className="text-gray-500">Serial Number:</span>
              <span className="ml-2 font-medium">{device.serial_number}</span>
            </div>

            {/* Device Type */}
            <div>
              <span className="text-gray-500">Device Type:</span>
              <span className={`ml-2 badge ${device.device_type === 'physical' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                {device.device_type}
              </span>
            </div>

            {/* Created By */}
            <div>
              <span className="text-gray-500">Created By:</span>
              <span className="ml-2 font-medium">{device.created_by_name || '-'}</span>
            </div>

            {/* Created At */}
            <div>
              <span className="text-gray-500">Created At:</span>
              <span className="ml-2">{formatDate(device.created_at)}</span>
            </div>

            {/* Last Seen */}
            <div>
              <span className="text-gray-500">Last Seen:</span>
              <span className="ml-2">{formatDate(device.last_seen_at)}</span>
            </div>

            {/* Assigned User */}
            <div>
              <span className="text-gray-500">Assigned User:</span>
              {device.assigned_patient_id ? (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {device.assigned_user_name || `${device.assigned_patient_id.slice(0, 8)}...`}
                </span>
              ) : (
                <span className="ml-2 text-gray-400">None</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Serial Number - only for create */}
      {!isEdit && (
        <div>
          <label htmlFor="serial_number" className="block text-sm font-medium text-gray-700 mb-1">
            Serial Number *
          </label>
          <input
            type="text"
            id="serial_number"
            name="serial_number"
            className="input"
            value={formData.serial_number}
            onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
            placeholder="RP-2024-001"
            required
          />
        </div>
      )}

      {/* Device Type - only for create */}
      {!isEdit && (
        <div>
          <label htmlFor="device_type" className="block text-sm font-medium text-gray-700 mb-1">
            Device Type *
          </label>
          <select
            id="device_type"
            name="device_type"
            className="input"
            value={formData.device_type}
            onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
            required
          >
            <option value="physical">Physical Device</option>
            <option value="virtual">Virtual Device (Testing)</option>
          </select>
        </div>
      )}

      {/* Institution - for create and edit (system_admin only) */}
      {hasRole('system_admin') && (
        <div>
          <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
            Institution *
          </label>
          <select
            id="institution"
            name="institution"
            className="input"
            value={formData.institution_id}
            onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
            required
          >
            <option value="">Select institution</option>
            {institutions?.data?.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.institution_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location
        </label>
        <input
          type="text"
          className="input"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="Room 101, Floor 2"
        />
      </div>

      {/* Location Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location Type
        </label>
        <select
          className="input"
          value={formData.location_type}
          onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
        >
          <option value="hospital">Hospital/Clinic</option>
          <option value="home">Patient Home</option>
          <option value="lab">Research Lab</option>
        </select>
      </div>

      {/* Status - edit only */}
      {isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="input"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="demo">Demo</option>
            <option value="retired">Retired</option>
          </select>
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          className="input min-h-[80px]"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes about the device..."
          rows={3}
        />
        <p className="text-xs text-gray-500 mt-1">
          Optional notes about the device, maintenance history, etc.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const DevicesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { hasRole } = useAuth()

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null)
  const [newDeviceData, setNewDeviceData] = useState<{ apiKey: string; deviceId: string } | null>(null)
  const [isLoadingDevice, setIsLoadingDevice] = useState(false)
  const [assignTargetDevice, setAssignTargetDevice] = useState<Device | null>(null)
  const [selectedAssignUserId, setSelectedAssignUserId] = useState('')

  // Function to fetch full device details before editing
  const openEditModal = async (device: Device) => {
    setIsLoadingDevice(true)
    try {
      const response = await devicesApi.get(device.id)
      setSelectedDevice(response.data)
      setIsModalOpen(true)
    } catch (error) {
      console.error('Failed to fetch device details:', error)
      // Fallback to list data if fetch fails
      setSelectedDevice(device)
      setIsModalOpen(true)
    } finally {
      setIsLoadingDevice(false)
    }
  }

  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [showVirtual, setShowVirtual] = useState(false)

  // Fetch devices
  const { data, isLoading } = useQuery({
    queryKey: ['devices', statusFilter],
    queryFn: () => devicesApi.list({
      status: statusFilter || undefined,
    }),
  })

  const { data: assignableUsersData, isLoading: isLoadingAssignableUsers } = useQuery({
    queryKey: ['device-assignable-users', assignTargetDevice?.id],
    queryFn: () => devicesApi.listAssignableUsers(assignTargetDevice!.id),
    enabled: !!assignTargetDevice,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: DeviceCreate) => devicesApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      setIsModalOpen(false)
      // Show API key and device ID
      const deviceResponse = response.data as DeviceCreateResponse
      setNewDeviceData({
        apiKey: deviceResponse.api_key,
        deviceId: deviceResponse.id,
      })
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeviceUpdate }) =>
      devicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      setIsModalOpen(false)
      setSelectedDevice(null)
    },
  })

  // Regenerate API key mutation
  const regenerateKeyMutation = useMutation({
    mutationFn: (id: string) => devicesApi.regenerateApiKey(id),
    onSuccess: (response) => {
      const deviceResponse = response.data as DeviceCreateResponse
      setNewDeviceData({
        apiKey: deviceResponse.api_key,
        deviceId: deviceResponse.id,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => devicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })

  const disableMutation = useMutation({
    mutationFn: (device: Device) => devicesApi.update(device.id, { status: 'retired' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })

  // Unassign patient mutation
  const unassignPatientMutation = useMutation({
    mutationFn: (id: string) => devicesApi.unassignPatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })

  const assignUserMutation = useMutation({
    mutationFn: ({ deviceId, userId }: { deviceId: string; userId: string }) =>
      devicesApi.assignUser(deviceId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      if (assignTargetDevice) {
        queryClient.invalidateQueries({ queryKey: ['device-assignable-users', assignTargetDevice.id] })
      }
      setAssignTargetDevice(null)
      setSelectedAssignUserId('')
    },
  })

  // Helper function for relative time display
  const formatLastSeen = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never'

    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`

    return date.toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Table columns with sorting enabled
  const columns: Column<Device>[] = [
    {
      key: 'serial_number',
      header: 'Serial Number',
      accessor: 'serial_number',
      sortable: true,
      searchable: true,
    },
    {
      key: 'device_type',
      header: 'Type',
      sortable: true,
      sortFn: (a, b) => (a.device_type || '').localeCompare(b.device_type || ''),
      render: (device) => (
        <span className={`badge ${device.device_type === 'physical' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
          {device.device_type}
        </span>
      ),
    },
    {
      key: 'institution',
      header: 'Institution',
      sortable: true,
      sortFn: (a, b) => (a.institution_name || '').localeCompare(b.institution_name || ''),
      searchable: true,
      searchFn: (item, query) => (item.institution_name || '').toLowerCase().includes(query),
      render: (device) => device.institution_name || '-',
    },
    {
      key: 'assigned_patient',
      header: 'Assigned User',
      render: (device) => device.assigned_patient_id ? (
        <div className="space-y-1">
          <div className="text-sm font-medium">
            {device.assigned_user_name || `${device.assigned_patient_id.slice(0, 8)}...`}
          </div>
          {device.assigned_user_provider && (
            <span className={`badge ${
              device.assigned_user_provider === 'local'
                ? 'bg-emerald-100 text-emerald-800'
                : device.assigned_user_provider === 'keycloak'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {device.assigned_user_provider}
            </span>
          )}
        </div>
      ) : (
        <span className="text-gray-400">-</span>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      sortFn: (a, b) => (a.location || '').localeCompare(b.location || ''),
      searchable: true,
      searchFn: (item, query) => (item.location || '').toLowerCase().includes(query),
      render: (device) => device.location || '-',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortFn: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (device) => (
        <span className={`badge ${
          device.status === 'active' ? 'badge-active' :
          device.status === 'maintenance' ? 'badge-warning' :
          device.status === 'demo' ? 'bg-blue-100 text-blue-800' :
          device.status === 'retired' ? 'bg-gray-100 text-gray-800' :
          'badge-inactive'
        }`}>
          {device.status}
        </span>
      ),
    },
    {
      key: 'last_seen',
      header: 'Last Seen',
      sortable: true,
      sortFn: (a, b) => {
        const aTime = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0
        const bTime = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0
        return aTime - bTime
      },
      render: (device) => {
        const lastSeen = formatLastSeen(device.last_seen_at)
        const isRecent = device.last_seen_at &&
          (new Date().getTime() - new Date(device.last_seen_at).getTime()) < 300000 // 5 minutes
        return (
          <span className={isRecent ? 'text-green-600 font-medium' : ''}>
            {lastSeen}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (device) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              openEditModal(device)
            }}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
            disabled={isLoadingDevice}
          >
            {isLoadingDevice ? 'Loading...' : 'Edit'}
          </button>
          {device.assigned_patient_id ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (confirm('Unassign user from this device?')) {
                  unassignPatientMutation.mutate(device.id)
                }
              }}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
              disabled={unassignPatientMutation.isPending}
            >
              Unassign
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setAssignTargetDevice(device)
                setSelectedAssignUserId('')
              }}
              className="text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
              Assign User
            </button>
          )}
          {hasRole('system_admin') && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (confirm('Regenerate API key? The old key will stop working immediately.')) {
                  regenerateKeyMutation.mutate(device.id)
                }
              }}
              className="text-orange-600 hover:text-orange-800 text-sm font-medium"
            >
              New Key
            </button>
          )}
          {device.device_type === 'physical' && device.status !== 'retired' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (confirm('Disable this physical device? It will be marked as retired.')) {
                  disableMutation.mutate(device)
                }
              }}
              className="text-orange-700 hover:text-orange-800 text-sm font-medium"
              disabled={disableMutation.isPending}
            >
              Disable
            </button>
          )}
          {device.device_type === 'virtual' && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (confirm('Delete this virtual device? This cannot be undone.')) {
                  deleteMutation.mutate(device.id)
                }
              }}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
              disabled={deleteMutation.isPending}
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ]

  // Handle form submit
  const handleSubmit = (formData: DeviceCreate | DeviceUpdate) => {
    if (selectedDevice) {
      updateMutation.mutate({ id: selectedDevice.id, data: formData as DeviceUpdate })
    } else {
      createMutation.mutate(formData as DeviceCreate)
    }
  }

  const assignableUsers = assignableUsersData?.data || []

  const filteredDevices = (data?.data || []).filter((device) =>
    showVirtual ? true : device.device_type !== 'virtual'
  )

  const sortedDevices = [...filteredDevices].sort((a, b) => {
    const typeOrder: Record<string, number> = { physical: 0, virtual: 1 }
    const aOrder = typeOrder[a.device_type] ?? 99
    const bOrder = typeOrder[b.device_type] ?? 99
    if (aOrder !== bOrder) {
      return aOrder - bOrder
    }
    return (a.serial_number || '').localeCompare(b.serial_number || '')
  })

  const handleAssignUser = () => {
    if (!assignTargetDevice || !selectedAssignUserId) {
      return
    }
    assignUserMutation.mutate({
      deviceId: assignTargetDevice.id,
      userId: selectedAssignUserId,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Devices</h2>
          <p className="text-sm text-gray-600">
            Manage RehaPiano devices and their configurations.
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedDevice(null)
            setIsModalOpen(true)
          }}
          className="btn-primary"
        >
          Add Device
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="w-48">
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="maintenance">Maintenance</option>
              <option value="demo">Demo</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
              checked={showVirtual}
              onChange={(e) => setShowVirtual(e.target.checked)}
            />
            Show virtual devices
          </label>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={sortedDevices}
        loading={isLoading}
        getRowKey={(device) => device.id}
        emptyMessage="No devices found"
        searchable
        searchPlaceholder="Search by serial number, institution, location..."
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedDevice(null)
        }}
        title={selectedDevice ? 'Edit Device' : 'Add Device'}
        size="lg"
      >
        <DeviceForm
          device={selectedDevice}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false)
            setSelectedDevice(null)
          }}
          loading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={!!assignTargetDevice}
        onClose={() => {
          setAssignTargetDevice(null)
          setSelectedAssignUserId('')
        }}
        title="Assign User"
        size="md"
      >
        <AssignUserModal
          device={assignTargetDevice}
          users={assignableUsers}
          loading={isLoadingAssignableUsers}
          saving={assignUserMutation.isPending}
          selectedUserId={selectedAssignUserId}
          onUserChange={setSelectedAssignUserId}
          onAssign={handleAssignUser}
          onClose={() => {
            setAssignTargetDevice(null)
            setSelectedAssignUserId('')
          }}
        />
      </Modal>

      {/* API Key Display Modal */}
      <Modal
        isOpen={!!newDeviceData}
        onClose={() => setNewDeviceData(null)}
        title="Device Created Successfully"
        size="lg"
        closeOnBackdrop={false}
      >
        {newDeviceData && (
          <ApiKeyDisplay
            apiKey={newDeviceData.apiKey}
            deviceId={newDeviceData.deviceId}
            onClose={() => setNewDeviceData(null)}
          />
        )}
      </Modal>
    </div>
  )
}

export default DevicesPage
